import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "bullio:chunk-reload";

/**
 * React.lazy for a route, with one recovery step.
 *
 * After a deploy the old chunk files are gone, so a tab opened before it fails
 * to load the next page it navigates to. One full reload fetches the new
 * index.html and its new chunk names. The session flag stops a reload loop if
 * the chunk is genuinely unreachable (offline): the second failure is thrown
 * to the error boundary as before.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyPage<T extends ComponentType<any>>(load: () => Promise<{ default: T }>) {
  return lazy(() =>
    load().then(
      (mod) => {
        try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* storage blocked */ }
        return mod;
      },
      (err) => {
        let reloaded = false;
        try { reloaded = sessionStorage.getItem(RELOAD_KEY) === "1"; } catch { /* storage blocked */ }
        if (!reloaded) {
          try { sessionStorage.setItem(RELOAD_KEY, "1"); } catch { /* storage blocked */ }
          window.location.reload();
          return new Promise<never>(() => {});
        }
        throw err;
      },
    ),
  );
}
